import React from 'react';
import { cn } from '@/lib/utils';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { AnimatedFormError } from '@/components/form/AnimatedFormError';
import {
  Button,
  NumberInput,
  Badge,
  HelpButton,
} from '@/components/ui';
import { fieldDescriptions } from './helpsheets/loadDescriptions';
import { Settings } from 'lucide-react';
import { sectionHeader, sectionUnderline, fieldLabelClass, fieldSublabelClass } from './roleFormStyles';

export interface DayRateConfigFormProps {
  // Data
  title: string;
  description: string;
  minimumBillableHours: number | null;
  halfDayHours: number | null;
  validMinimumBillableHoursForHalfDay: number;

  // Handlers
  onMinimumBillableHoursChange: (value: number) => void;
  onHalfDayHoursChange: (value: number) => void;

  // Props for controlled inputs
  minimumBillableHoursProps: Record<string, unknown>;
  halfDayHoursProps: Record<string, unknown>;

  // Validation
  minimumBillableHoursError?: React.ReactNode;
  halfDayHoursError?: React.ReactNode;
  isCustomized?: boolean;
  onRevert?: () => void;
  isMinimumBillableHoursDisabled?: boolean;
  isHalfDayConfigDisabled?: boolean;

  // Context for dynamic labels
  context?: 'user' | 'client';
  integrated?: boolean;
}

export const DayRateConfigForm: React.FC<DayRateConfigFormProps> = ({
  title,
  description,
  validMinimumBillableHoursForHalfDay,
  minimumBillableHoursProps,
  halfDayHoursProps,
  minimumBillableHoursError,
  halfDayHoursError,
  isCustomized,
  onRevert,
  context = 'user',
  isMinimumBillableHoursDisabled,
  isHalfDayConfigDisabled,
  integrated = false,
}) => {
  const labels = {
    minimumBillableHours: {
      title: "Minimum Billable Hours",
      description: context === 'user'
        ? "The number of hours included in your day rate. You earn the full rate even if a shift ends early."
        : "Standard hours included in a typical Day Rate booking for this client."
    },
    halfDayHours: {
      title: "Half-Day Threshold",
      description: "Maximum shift length that qualifies as a half day. Longer shifts are billed at the full day rate."
    },
  };

  return (
    <div className={cn("space-y-4")}>
        {!integrated && (
          <div className={cn("space-y-2 text-left transition-opacity", (isMinimumBillableHoursDisabled && isHalfDayConfigDisabled) && "opacity-50")}>
            <div className="flex items-center justify-start gap-3">
              <div className={cn(sectionHeader, sectionUnderline, "flex items-center gap-2 mb-0")}>
                <Settings className="w-3.5 h-3.5" />
                <span>{title}</span>
              </div>
              {isCustomized && <Badge variant="default">{context === 'client' ? 'Client' : 'Profile'} Override</Badge>}
            </div>
            <p className={cn(fieldSublabelClass)}>{description}</p>
          </div>
        )}

        <fieldset disabled={isMinimumBillableHoursDisabled}>
          <div
            className={cn(
              "w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center lg:grid-cols-2 lg:gap-x-6",
              "transition-opacity", isMinimumBillableHoursDisabled && "opacity-50"
            )}
          >
            <div className="min-w-0 flex flex-col justify-start">
              <div className="flex items-center gap-2">
                <label htmlFor="minimumBillableHours" className={fieldLabelClass}>{labels.minimumBillableHours.title}</label>
                <HelpButton
                  fieldName="Minimum Billable Hours"
                  content={fieldDescriptions.minimum_billable_hours}
                />
              </div>
              <p className={cn(fieldSublabelClass, "mt-0 hidden lg:block")}>
                {labels.minimumBillableHours.description}
              </p>
            </div>
            <div className="flex flex-col items-end justify-center self-center sm:items-start lg:items-end lg:w-full">
              <NumberInput
                id="minimumBillableHours"
                unit="hrs"
                unitPosition="right"
                size="xs"
                className="ml-auto font-mono"
                {...minimumBillableHoursProps}
                min={1}
                max={24}
                step={0.5}
              />
              <AnimatedFormError className="w-full text-left md:text-right mt-1">
                {minimumBillableHoursError}
              </AnimatedFormError>
            </div>
            <p className={cn(fieldSublabelClass, "col-span-2 lg:hidden")}>
              {labels.minimumBillableHours.description}
            </p>
          </div>
        </fieldset>

        <fieldset disabled={isHalfDayConfigDisabled}>
          <div className="space-y-6">
            {isHalfDayConfigDisabled && (
              <p className="text-xs text-muted-foreground italic">
                Enable <span className="font-medium not-italic">Half-Day Billing</span> on a role above to configure half-day threshold.
              </p>
            )}
            <AnimatedCollapse open={!isHalfDayConfigDisabled} className="space-y-6">
            <div
              className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center lg:grid-cols-2 lg:gap-x-6"
            >
              <div className="min-w-0 flex flex-col justify-start">
                <div className="flex items-center gap-2">
                  <label htmlFor="halfDayHours" className={fieldLabelClass}>{labels.halfDayHours.title}</label>
                  <HelpButton
                    fieldName="Half-Day Threshold"
                    content={fieldDescriptions.half_day_hours}
                  />
                </div>
                <p className={cn(fieldSublabelClass, "mt-0 hidden lg:block")}>
                  {labels.halfDayHours.description}
                </p>
              </div>
              <div className="flex flex-col items-end justify-center self-center sm:items-start lg:items-end lg:w-full">
                <NumberInput
                  id="halfDayHours"
                  unit="hrs"
                  unitPosition="right"
                  size="xs"
                  className="ml-auto font-mono"
                  placeholder={validMinimumBillableHoursForHalfDay > 0 ? String(Math.floor(validMinimumBillableHoursForHalfDay / 2)) : undefined}
                  {...halfDayHoursProps}
                  min={1}
                  max={validMinimumBillableHoursForHalfDay > 0 ? validMinimumBillableHoursForHalfDay - 0.5 : undefined}
                  step={0.5}
                />
                <AnimatedFormError className="w-full text-left md:text-right mt-1">
                  {halfDayHoursError}
                </AnimatedFormError>
              </div>
              <p className={cn(fieldSublabelClass, "col-span-2 lg:hidden")}>
                {labels.halfDayHours.description}
              </p>
            </div>
            </AnimatedCollapse>
          </div>
        </fieldset>

        <AnimatedCollapse open={!!(isCustomized && onRevert)}>
              <div className="pt-4 border-t flex justify-center">
                <Button
                  variant="link"
                  className="p-0 h-auto text-muted-foreground"
                  onClick={onRevert}
                  type="button"
                  aria-label="Revert billing structure settings to profile defaults"
                >
                  Revert to Profile Defaults
                </Button>
              </div>
        </AnimatedCollapse>
    </div>
  );
};
